/**
 * <hydrate-slp-stores> — Layer 3 consumer custom element for the SRP
 * Partner Store results module.
 *
 * Renders partner-store cards built from the APIM store-search result produced
 * by the existing hydrateSLP pipeline.
 *
 * Architecture position:
 *   Layer 1: query-store-list.js    (raw APIM fetch)
 *   Layer 2: hydrate-callers.js     (orchestration + `theme:search:hydrated` dispatch)
 *   Layer 3: hydrate-slp-stores.js  (this module — renders cards + pagination)
 *
 * Data source:
 *   - `window.__ngHydration` — the cached result, set by the auto-bootstrap in
 *     snippets/scripts.liquid once hydrateSLP resolves (race-condition guard).
 *   - `theme:search:stores:hydrated` CustomEvent — the eager per-module event
 *     dispatched the moment the store search resolves, so this module paints
 *     without waiting for the product search (MA-1367).
 *   - `theme:search:hydrated` CustomEvent on `document` — the combined event
 *     dispatched by hydrate-callers.js once every query resolves (re-fetches,
 *     filter changes). An envelope already rendered from the eager event only
 *     refreshes the back label (which needs the products envelope).
 *
 * Collapsed / Expanded views:
 *   Collapsed (default): if the stores fit within the configured rows for the
 *   current breakpoint, a static grid. Otherwise the same cards become a
 *   horizontally scrollable multi-row track (CSS scroll-snap) with the
 *   desktop-only circular cut-out arrows used by the theme's recommendation
 *   carousels (see sections/hydrate-slp-products.liquid zero-results state).
 *
 *   Expanded ("View All Stores"): hides the sibling product/filter sections and
 *   shows the results count, a full grid, "Back to Product Results", and
 *   pagination. The URL carries `?view=stores` (history.pushState) so the
 *   expanded state survives reload and participates in back/forward.
 *
 * Pagination (expanded mode):
 *   The APIM response includes `pagination: { page, totalPages }`. Page changes
 *   re-fetch via queryStoreList with the same field set + locale the bootstrap
 *   uses, so cards keep their conditions/reward data on every page.
 */

import { renderStoreCard, initStoreCardInteractions } from "@theme/ng-store-card";
import { queryStoreList } from "@theme/query-store-list";
import { trackStoreSearch } from "@theme/hydrate-callers";
import { getStoreApiLocale } from "@theme/locale-map";
import { htmlEscape, mediaQueryLarge, isDesktopBreakpoint } from "@theme/utilities";
import {
  renderPagination,
  bindPaginationEvents,
  readPaginationLabels,
  scrollToWithHeaderOffset,
} from "@theme/hydrate-pagination";

/** @param {string} key @param {string} fallback @returns {string} */
const t = (key, fallback) => Theme?.translations?.[key] || fallback;

/**
 * Store-search fields the card renderer needs. Must stay in sync with the
 * `storeParams.fields` list the SRP bootstrap passes to hydrateSLP in
 * snippets/scripts.liquid (`maStoreConditions` is not in the API defaults).
 */
const STORE_FIELDS = [
  "volumeId",
  "ibvOnly",
  "catalogLogo",
  "ibvStandard",
  "convIbvStandard",
  "convIbvOnly",
  "channelbitsDelim",
  "wwwLink",
  "name",
  "derived",
  "labels",
  "rewards",
  "maStoreConditions",
  "productType",
  "stdCashback",
  "incCashback",
  "walkIn",
  "venueId",
  "address",
  "description",
  "volumeName",
];

/** Columns shown on mobile (fixed 2-up). */
const MOBILE_COLUMNS = 2;

/** Desktop-only circular cut-out arrow — same look as the theme's
 * recommendation carousels (white circle, #DBDBDB border, shadow, chevron). */
const arrowButton = (direction, label) => {
  const side = direction === "prev" ? "tw:left-0 tw:-translate-x-1/2" : "tw:right-0 tw:translate-x-1/2";
  const flip = direction === "next" ? " tw:rotate-180" : "";
  return `<button
      type="button"
      data-store-carousel-${direction}
      aria-label="${label}"
      class="tw:absolute ${side} tw:top-1/2 tw:z-10 tw:-translate-y-1/2 tw:hidden tw:lg:flex tw:items-center tw:justify-center tw:w-10 tw:h-10 tw:shrink-0 tw:rounded-full tw:border tw:border-[#DBDBDB] tw:bg-white tw:shadow-md tw:p-0 tw:text-coal tw:cursor-pointer tw:transition-opacity tw:disabled:cursor-default"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" class="${flip.trim()}">
        <path d="M15 18L9 12L15 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;
};

class HydrateSlpStores extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onHydrated = null;

  /** @type {(() => void) | null} */
  #onPopState = null;

  /** @type {((event: MediaQueryListEvent) => void) | null} */
  #onBreakpointChange = null;

  /** @type {ResizeObserver | null} */
  #trackResizeObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #expanded = false;

  /** @type {Object[]} */
  #allStores = [];

  /** @type {string} */
  #query = "";

  /** @type {number} */
  #totalCount = 0;

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #totalPages = 0;

  /** Whether the current search also returned product results — drives the
   * expanded view's back label ("Product Results" vs "Search Results"). */
  #hasProducts = true;

  /** Envelope rendered by the eager per-module event — the combined
   * `theme:search:hydrated` event re-delivers the same object; only the back
   * label (which needs the products envelope) is refreshed then.
   * @type {Object | null} */
  #lastRenderedEnvelope = null;

  connectedCallback() {
    this.#onHydrated = event => {
      const detail = /** @type {CustomEvent} */ (event).detail;
      this.#render(detail);
    };
    // Eager per-module event (stores resolved — products may still be in
    // flight) plus the combined event (re-fetches, filter changes, race guard).
    document.addEventListener("theme:search:stores:hydrated", this.#onHydrated);
    document.addEventListener("theme:search:hydrated", this.#onHydrated);

    // The static-vs-carousel decision and the expanded page size both depend
    // on the breakpoint, so re-render the active view when crossing it.
    this.#onBreakpointChange = () => {
      if (!this.#allStores.length) return;
      if (this.#expanded) {
        // Page size changed with the breakpoint — restart from page 1.
        this.#currentPage = 1;
        this.#totalPages = Math.max(1, Math.ceil(this.#totalCount / this.#pageSize));
        this.#renderExpandedCards(this.#allStores.slice(0, this.#pageSize));
        if (this.#allStores.length < Math.min(this.#pageSize, this.#totalCount)) {
          this.#onPageChange(1);
        }
        this.#updatePagination();
      } else {
        this.#renderCollapsedView(this.#allStores);
      }
    };
    mediaQueryLarge.addEventListener("change", this.#onBreakpointChange);

    // Browser back/forward: keep the view in sync with the ?view=stores param.
    this.#onPopState = () => {
      if (!this.#allStores.length) return;
      const wantsStores = new URLSearchParams(window.location.search).get("view") === "stores";
      if (wantsStores !== this.#expanded) {
        this.#setExpanded(wantsStores, { pushState: false });
      }
    };
    window.addEventListener("popstate", this.#onPopState);

    // Delegated store-card interactions (special-conditions trigger).
    initStoreCardInteractions(this);

    // Pagination click events (delegated listener on the nav element).
    const paginationNav = this.#paginationNav;
    if (paginationNav) {
      bindPaginationEvents(paginationNav, page => this.#onPageChange(page));
    }

    this.#bindViewAll();
    this.#bindBack();

    // Race guard: if hydrateSLP resolved before this element connected, the
    // event has already fired and been missed — process the cached result now.
    if (window.__ngStoreHydration) {
      this.#render(window.__ngStoreHydration);
    } else if (window.__ngHydration) {
      this.#render(window.__ngHydration);
    } else if (this.#isDesignMode) {
      this.hidden = false;
    }
  }

  disconnectedCallback() {
    if (this.#onHydrated) {
      document.removeEventListener("theme:search:stores:hydrated", this.#onHydrated);
      document.removeEventListener("theme:search:hydrated", this.#onHydrated);
    }
    if (this.#onPopState) window.removeEventListener("popstate", this.#onPopState);
    if (this.#onBreakpointChange) mediaQueryLarge.removeEventListener("change", this.#onBreakpointChange);
    this.#trackResizeObserver?.disconnect();
    this.#abortController?.abort();
  }

  /* --- DOM refs ---------------------------------------------------------- */

  get #collapsedContainer() {
    return this.querySelector("[data-slp-store-collapsed]");
  }

  get #collapsedGrid() {
    return this.querySelector("[data-slp-store-grid]");
  }

  get #expandedContainer() {
    return this.querySelector("[data-slp-expanded]");
  }

  get #expandedGrid() {
    return this.querySelector("[data-slp-expanded-grid]");
  }

  get #storeCountEls() {
    return this.querySelectorAll("[data-slp-store-count]");
  }

  get #viewAllLinks() {
    return this.querySelectorAll("[data-slp-view-all]");
  }

  get #backBtn() {
    return this.querySelector("[data-slp-back]");
  }

  get #paginationNav() {
    return this.querySelector("[data-slp-store-pagination]");
  }

  /** Whether the storefront is being viewed in the Shopify theme editor. */
  get #isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /* --- Layout settings ---------------------------------------------------- */

  get #isDesktop() {
    return isDesktopBreakpoint();
  }

  get #rowsDesktop() {
    return parseInt(this.getAttribute("data-rows-desktop") || "2", 10) || 2;
  }

  get #rowsMobile() {
    return parseInt(this.getAttribute("data-rows-mobile") || "3", 10) || 3;
  }

  get #columnsDesktop() {
    return parseInt(this.getAttribute("data-columns-desktop") || "5", 10) || 5;
  }

  /** Stores per page for expanded-mode pagination fetches (per breakpoint). */
  get #pageSize() {
    const attr = this.#isDesktop ? "data-store-page-size" : "data-store-page-size-mobile";
    const fallback = this.#isDesktop ? 25 : 10;
    return parseInt(this.getAttribute(attr) || "", 10) || fallback;
  }

  /**
   * Maximum cards visible in the collapsed static grid for the current
   * breakpoint (rows × columns).
   */
  get #collapsedLimit() {
    return this.#isDesktop ? this.#rowsDesktop * this.#columnsDesktop : this.#rowsMobile * MOBILE_COLUMNS;
  }

  /* --- View All / Back binding ------------------------------------------ */

  #bindViewAll() {
    for (const link of this.#viewAllLinks) {
      link.addEventListener("click", event => {
        event.preventDefault();
        this.#setExpanded(true);
      });
    }
  }

  #bindBack() {
    this.#backBtn?.addEventListener("click", event => {
      event.preventDefault();
      this.#setExpanded(false);
    });
  }

  /* --- Expand / Collapse ------------------------------------------------ */

  /**
   * Switches between the collapsed module and the expanded store-results view.
   * Kept as the single isolated entry point for the "View All Stores" action.
   *
   * @param {boolean} expanded
   * @param {{ pushState?: boolean, scroll?: boolean }} [options]
   */
  #setExpanded(expanded, { pushState = true, scroll = true } = {}) {
    this.#expanded = expanded;

    if (pushState) {
      const url = new URL(window.location.href);
      if (expanded) {
        url.searchParams.set("view", "stores");
      } else {
        url.searchParams.delete("view");
      }
      history.pushState(null, "", url.toString());
    }

    const collapsed = this.#collapsedContainer;
    const expandedEl = this.#expandedContainer;
    if (collapsed) collapsed.hidden = expanded;
    if (expandedEl) expandedEl.hidden = !expanded;

    if (expanded) {
      // The cached stores come from the bootstrap search request, whose page
      // size differs from the section's "stores per page" setting — render
      // only one configured page's worth, and fetch page 1 at the configured
      // size when the cache holds fewer stores than a full expanded page.
      this.#renderExpandedCards(this.#allStores.slice(0, this.#pageSize));
      this.#updateStoreCount();
      if (this.#allStores.length < Math.min(this.#pageSize, this.#totalCount)) {
        this.#onPageChange(1);
      }
    } else {
      this.#currentPage = 1;
    }

    this.#updatePagination();

    // The expanded store view replaces the product results (and their filters).
    this.#toggleSiblings(expanded);

    if (scroll) scrollToWithHeaderOffset(this);
  }

  /**
   * Hides the module entirely, restoring the collapsed state and the sibling
   * product/filter sections first — a re-hydration can remove the module
   * while the expanded store view is active (e.g. filter changes), and the
   * products must come back when it does.
   */
  #hideModule() {
    this.#expanded = false;
    this.#currentPage = 1;
    this.#lastRenderedEnvelope = null;
    const collapsed = this.#collapsedContainer;
    const expandedEl = this.#expandedContainer;
    if (collapsed) collapsed.hidden = false;
    if (expandedEl) expandedEl.hidden = true;
    this.#updatePagination();
    this.#toggleSiblings(false);
    this.hidden = true;
  }

  /**
   * Positions this section relative to the product grid per the page-template
   * API's module order: "stores" before "products" places the module above
   * the products section (and its filter bar), otherwise below it. The
   * customizer-saved position stays as the fallback when no order is given.
   *
   * @param {Array<string> | null} moduleOrder
   */
  #applyModuleOrder(moduleOrder) {
    if (!moduleOrder || !moduleOrder.includes("products")) return;

    const ownSection = this.closest(".shopify-section");
    const productsEl = document.querySelector("hydrate-slp-products");
    const productsSection = productsEl?.closest(".shopify-section");
    if (!ownSection || !productsSection || !productsSection.parentElement) return;

    const storesFirst = moduleOrder.indexOf("stores") < moduleOrder.indexOf("products");

    if (storesFirst) {
      // The filter bar belongs to the products module — sit above it too.
      const filtersSection = document.querySelector("hydrate-filters")?.closest(".shopify-section");
      const anchor =
        filtersSection && filtersSection.parentElement === productsSection.parentElement
          ? filtersSection
          : productsSection;
      if (ownSection.nextElementSibling !== anchor) {
        productsSection.parentElement.insertBefore(ownSection, anchor);
      }
    } else if (productsSection.nextElementSibling !== ownSection) {
      productsSection.parentElement.insertBefore(ownSection, productsSection.nextElementSibling);
    }
  }

  /**
   * Hide/show the sibling product grid and filter bar sections.
   * @param {boolean} hide
   */
  #toggleSiblings(hide) {
    const siblings = document.querySelectorAll("hydrate-slp-products, hydrate-filters");
    for (const el of siblings) {
      /** @type {HTMLElement} */ (el).hidden = hide;
    }
  }

  #updateBackLabel() {
    const btn = this.#backBtn;
    const label = btn?.querySelector("[data-slp-back-label]");
    if (!btn || !label) return;
    const attr = this.#hasProducts ? "data-label-products" : "data-label-search";
    label.textContent = btn.getAttribute(attr) || label.textContent || "";
  }

  #updateStoreCount() {
    for (const el of this.#storeCountEls) {
      const template = htmlEscape(el.getAttribute("data-template") || "");
      // The search term renders medium-weight within the count line.
      el.innerHTML = template
        .replace("{count}", htmlEscape(String(this.#totalCount)))
        .replace("{query}", `<span class="tw:font-medium">${htmlEscape(this.#query)}</span>`);
    }
  }

  /* --- Render ------------------------------------------------------------ */

  /**
   * Renders store cards from a hydration result. Only acts on SLP-context
   * results; non-SLP contexts (typeahead/plp) are ignored.
   *
   * @param {Object | null | undefined} result
   */
  #render(result) {
    if (!result || result.context !== "slp") return;

    const envelope = /** @type {Record<string, any>} */ (result).stores;
    const stores = envelope?.ok && Array.isArray(envelope.data?.stores) ? envelope.data.stores : [];
    const productsEnvelope = /** @type {Record<string, any>} */ (result).products;

    // The page-template API decides which modules the SRP shows and in which
    // order. When it answers without "stores" — including the empty array of
    // its zero-results template — the module must not render; when it errors
    // or is absent, fall back to showing whenever there are store matches.
    // No store matches / store API error always hide it.
    const pageTemplate = /** @type {Record<string, any>} */ (result).pageTemplate;
    const moduleOrder =
      pageTemplate?.ok && Array.isArray(pageTemplate.data?.moduleOrder) ? pageTemplate.data.moduleOrder : null;

    if (!stores.length || (moduleOrder && !moduleOrder.includes("stores"))) {
      this.#hideModule();
      return;
    }

    // Combined event re-delivering the envelope already rendered from the
    // eager per-module event — just settle the back label now that the
    // products envelope is resolved.
    if (envelope === this.#lastRenderedEnvelope) {
      if (productsEnvelope !== undefined) {
        this.#hasProducts = Boolean(productsEnvelope?.ok && productsEnvelope.data?.totalCount > 0);
        this.#updateBackLabel();
      }
      return;
    }
    this.#lastRenderedEnvelope = envelope;

    this.#applyModuleOrder(moduleOrder);

    this.#query =
      /** @type {Record<string, any>} */ (result).query || new URLSearchParams(window.location.search).get("q") || "";
    this.#allStores = stores;
    this.#totalCount = envelope.data?.totalCount || stores.length;

    // Store-only searches label the back control "Back to Search Results" —
    // there are no product results to go back to. On the eager per-module
    // event the products envelope is still in flight (`undefined`) — derive
    // the label from the module order until the combined event settles it.
    this.#hasProducts =
      productsEnvelope !== undefined
        ? Boolean(productsEnvelope?.ok && productsEnvelope.data?.totalCount > 0)
        : moduleOrder
          ? moduleOrder.includes("products")
          : true;
    this.#updateBackLabel();

    // The response's pagination meta reflects the bootstrap request's page
    // size, not the section's "stores per page" setting — derive the expanded
    // view's page count from the total and the configured size instead.
    this.#currentPage = 1;
    this.#totalPages = Math.max(1, Math.ceil(this.#totalCount / this.#pageSize));

    // "View All Stores" only matters when there is more than the module shows.
    const hasMore = this.#totalCount > this.#collapsedLimit;
    for (const link of this.#viewAllLinks) {
      /** @type {HTMLElement} */ (link).hidden = !hasMore;
    }

    this.#updateStoreCount();
    this.#renderCollapsedView(stores);

    // Restore the expanded view when the URL carries ?view=stores. This runs on
    // every re-hydration (filter, sort, page change), where the module is
    // already visible — scrolling here would drag the shopper down to the
    // stores module instead of leaving them at the top of the new results.
    const wantsStores = new URLSearchParams(window.location.search).get("view") === "stores";
    this.#setExpanded(wantsStores, { pushState: false, scroll: false });

    this.hidden = false;
  }

  /**
   * Renders the collapsed view — static grid when the stores fit the row cap,
   * horizontally scrollable multi-row track otherwise.
   *
   * @param {Object[]} stores
   */
  #renderCollapsedView(stores) {
    const grid = this.#collapsedGrid;
    if (!grid) return;

    this.#trackResizeObserver?.disconnect();
    this.#trackResizeObserver = null;

    if (stores.length <= this.#collapsedLimit) {
      grid.innerHTML = this.#buildStaticGrid(stores);
    } else {
      grid.innerHTML = this.#buildCarousel(stores);
      this.#initCarouselNav();
    }
  }

  /**
   * Static grid — 5 columns (setting) on desktop, 2 on mobile.
   * @param {Object[]} stores
   * @returns {string}
   */
  #buildStaticGrid(stores) {
    const cards = stores.map(hit => renderStoreCard(hit)).join("");
    return `<ul class="tw:m-0 tw:grid tw:list-none tw:grid-cols-2 tw:gap-2.5 tw:p-0 tw:lg:grid-cols-[repeat(var(--slp-columns,5),minmax(0,1fr))] tw:lg:gap-5" role="list">${cards}</ul>`;
  }

  /**
   * Horizontally scrollable multi-row track. Column-major flow keeps the API
   * order reading down each column; the column width and row count come from
   * the section's CSS variables (see sections/hydrate-slp-stores.liquid).
   *
   * @param {Object[]} stores
   * @returns {string}
   */
  #buildCarousel(stores) {
    const cards = stores.map(hit => renderStoreCard(hit)).join("");
    return `<div class="tw:relative" data-store-carousel>
      ${arrowButton("prev", t("accessibility_option_scroll_left", "Scroll left"))}
      ${arrowButton("next", t("accessibility_option_scroll_right", "Scroll right"))}
      <ul
        data-store-track
        class="tw:m-0 tw:grid tw:list-none tw:grid-flow-col tw:auto-cols-(--slp-track-col-mobile) tw:grid-rows-[repeat(var(--slp-rows-mobile,3),auto)] tw:gap-2.5 tw:overflow-x-auto tw:scroll-smooth tw:snap-x tw:snap-mandatory tw:no-scrollbar tw:p-0 tw:lg:auto-cols-(--slp-track-col-desktop) tw:lg:grid-rows-[repeat(var(--slp-rows-desktop,2),auto)] tw:lg:gap-5"
        role="list"
      >${cards}</ul>
    </div>`;
  }

  /**
   * Wires the carousel's prev/next arrows to the scroll track — same behaviour
   * as the theme's recommendation carousels: arrows only when the track
   * overflows, the unusable edge arrow fully hidden, one column per click.
   */
  #initCarouselNav() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector("[data-store-track]"));
    const prevBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-store-carousel-prev]"));
    const nextBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-store-carousel-next]"));
    if (!track || !prevBtn || !nextBtn) return;

    const columnStep = () => {
      const card = track.querySelector("li");
      if (!card) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const setEdgeState = (button, atEdge) => {
      button.disabled = atEdge;
      button.style.opacity = atEdge ? "0" : "1";
      button.style.pointerEvents = atEdge ? "none" : "";
    };

    const updateArrows = () => {
      // +2px guards against sub-pixel rounding when the cards exactly fill the track.
      const canScroll = track.scrollWidth > track.clientWidth + 2;
      prevBtn.hidden = nextBtn.hidden = !canScroll;
      if (!canScroll) return;
      setEdgeState(prevBtn, track.scrollLeft <= 1);
      setEdgeState(nextBtn, track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
    };

    prevBtn.addEventListener("click", () => track.scrollBy({ left: -columnStep(), behavior: "smooth" }));
    nextBtn.addEventListener("click", () => track.scrollBy({ left: columnStep(), behavior: "smooth" }));
    track.addEventListener("scroll", updateArrows, { passive: true });

    if ("ResizeObserver" in window) {
      this.#trackResizeObserver = new ResizeObserver(updateArrows);
      this.#trackResizeObserver.observe(track);
    }

    updateArrows();
  }

  /**
   * Renders the expanded grid for the current page of stores.
   * @param {Object[]} stores
   */
  #renderExpandedCards(stores) {
    const grid = this.#expandedGrid;
    if (grid) grid.innerHTML = stores.map(hit => renderStoreCard(hit)).join("");
  }

  /** Updates the pagination nav visibility and content (expanded mode only). */
  #updatePagination() {
    const paginationNav = this.#paginationNav;
    if (!paginationNav) return;

    if (this.#expanded && this.#totalPages > 1) {
      paginationNav.innerHTML = renderPagination(
        this.#currentPage,
        this.#totalPages,
        readPaginationLabels(paginationNav)
      );
      paginationNav.hidden = false;
    } else {
      paginationNav.innerHTML = "";
      paginationNav.hidden = true;
    }
  }

  /* --- Pagination -------------------------------------------------------- */

  /**
   * Handles a pagination page-change request in expanded mode. Fetches the
   * requested page via queryStoreList with the same fields + locale as the
   * bootstrap request, so every page renders complete cards.
   *
   * @param {number} page - 1-based target page number.
   */
  async #onPageChange(page) {
    if (!this.#expanded) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const result = await queryStoreList({
        query: this.#query,
        page,
        size: this.#pageSize,
        fields: STORE_FIELDS,
        ...getStoreApiLocale(),
        signal: this.#abortController.signal,
      });

      // Page 1 is reported by the SLP lifecycle; without this, every page after
      // the first was an unreported store search.
      trackStoreSearch(this.#query, "slp", result);

      if (!result.ok || !result.data) return;

      const stores = Array.isArray(result.data.stores) ? result.data.stores : [];
      this.#currentPage = result.data.pagination?.page || page;
      this.#totalPages = result.data.pagination?.totalPages || this.#totalPages;

      this.#renderExpandedCards(stores);
      this.#updatePagination();
      scrollToWithHeaderOffset(this);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.error("[slp-stores] page change failed", error);
    }
  }
}

if (!customElements.get("hydrate-slp-stores")) {
  customElements.define("hydrate-slp-stores", HydrateSlpStores);
}
